<template>
  <div id="cesium-container" ref="cesiumContainer">
    <div id="blurred-background" v-if="isViewerLoading || loading">
      <v-progress-circular
        id="loading-indicator"
        :size="40"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <cesium-recorder ref="cesiumRecorder" />
    <cesium-lighting-button ref="cesiumLightingButton" />
  </div>
</template>

<script>
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import { AnalyticalOrbit } from '../../components/ThreeD/AnalyticalOrbit'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import CesiumLightingButton from '../../components/ThreeD/CesiumLightingButton.vue'
import CesiumTools from '../../components/ThreeD/CesiumTools'
import CesiumConstellationsTools from '../../components/ThreeD/CesiumConstellationsTools'
import * as Cesium from 'cesium'

export default {
  name: 'numericalOrbit-cesium-viewer',
  mixins: [CesiumInitialization, AnalyticalOrbit],
  props: ['tracesResults', 'loading', 'orbitDate', 'timestep'],
  components: {
    'cesium-recorder': CesiumRecorder,
    'cesium-lighting-button': CesiumLightingButton
  },
  data() {
    return {
      isViewerLoading: false,
      colorsArray: [],
      sceneModeObserver: null,
      rotatingOrbitalPlanes: null,
      satellitesPositions: [],
      satellitesDS: null
    }
  },
  watch: {
    tracesResults: {
      handler(traceData) {
        if (!traceData) {
          return
        }
        this.updateTimes()
        this.updateTracesWithSceneMode(this.viewer?.scene?.mode)
      },
      deep: true
    }
  },
  async mounted() {
    this.isViewerLoading = true
    this.initializeViewer(true, true, true, false)
    this.insertElementInToolbar(this.$refs.cesiumLightingButton.$el)
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)
    this.$refs.cesiumLightingButton.toggleLighting()

    this.satellitesDS = new Cesium.CustomDataSource()
    this.viewer.dataSources.add(this.satellitesDS)

    this.orbitTracesPC = new Cesium.PolylineCollection()
    this.viewer.scene.primitives.add(this.orbitTracesPC)

    this.rotatingOrbitalPlanes = new Cesium.PrimitiveCollection()
    this.viewer.scene.primitives.add(this.rotatingOrbitalPlanes)

    this.sceneModeObserver = Cesium.knockout
      .getObservable(this.viewer.sceneModePicker.viewModel, 'sceneMode')
      .subscribe(this.updateTracesWithSceneMode)

    this.viewer.clock.shouldAnimate = true
    this.viewer.clock.onTick.addEventListener(this.computeIcrfToFixed)
    this.computeIcrfToFixed(this.viewer.clock, true)
    this.viewer.scene.render(this.viewer.clock.currentTime)

    this.isViewerLoading = false
  },
  beforeDestroy() {
    this.sceneModeObserver.dispose()
  },
  methods: {
    async updateTimes() {
      const [startTime, stopTime] = this.getSimulationDates()

      await Cesium.Transforms.preloadIcrfFixed(
        new Cesium.TimeInterval({
          start: startTime,
          stop: stopTime,
          isStartIncluded: true,
          isStopIncluded: true
        })
      )
      this.setCesiumClock(startTime, stopTime, 30)
    },

    getSimulationDates() {
      let duration = this.timestep * (this.tracesResults['orbit'][0].length - 1)

      let startDate = Cesium.JulianDate.fromIso8601(this.orbitDate)
      let stopDate = new Cesium.JulianDate()
      Cesium.JulianDate.addSeconds(startDate, duration, stopDate)

      return [startDate, stopDate]
    },

    updateTracesWithSceneMode(sceneMode) {
      this.viewer.scene.postUpdate.removeEventListener(this.makeEarthSpin)
      this.updateSatellite(this.tracesResults['orbit'][0])
      if (sceneMode !== Cesium.SceneMode.SCENE3D) {
        this.updateGroundTraces(this.tracesResults['ground'])
        this.rotatingOrbitalPlanes.show = false
      } else {
        this.updateOrbitTraces(this.tracesResults['orbit'])
        this.rotatingOrbitalPlanes.show = true
        this.viewer.scene.postUpdate.addEventListener(this.makeEarthSpin)
      }
    },
    updateGroundTraces(traces) {
      this.isViewerLoading = true
      CesiumTools.removePrimitivesFromPC(this.orbitTracesPC)
      this.colorsArray = CesiumTools.createColorsArray(1)
      const index = 0
      const color = this.colorsArray[index]
      const polylineMaterial = this.createPolylineMaterial(false, color, 1)
      this.addOneAnalyticalTrace(
        this.orbitTracesPC,
        [].concat.apply([], traces[0]),
        polylineMaterial,
        true
      )

      this.isViewerLoading = false
    },
    updateOrbitTraces(traces) {
      this.isViewerLoading = true
      CesiumTools.removePrimitivesFromPC(this.orbitTracesPC)
      this.colorsArray = CesiumTools.createColorsArray(1)

      let positions = {}
      positions['geometry'] = {}
      positions['geometry']['plane-1-spacecraft-1'] = traces[0]
      this.satellitesPositions = positions
      this.addNumericalTracesWithoutTimeIntervals(this.satellitesPositions, {
        primitiveCollection: this.rotatingOrbitalPlanes,
        colorsArray: this.colorsArray
      })

      this.isViewerLoading = false
    },

    // -------------------------- SATELLITE
    updateSatellite(satPositions) {
      const satName = 'spacecraft'
      const color = this.colorsArray[0]
      let satEntity = this.initSatEntity(satPositions, satName, color)
      if (this.satellitesDS == null) {
        this.satellitesDS = new Cesium.CustomDataSource()
        this.viewer.dataSources.add(this.satellitesDS)
      }
      CesiumTools.removePrimitivesFromPC(this.satellitesDS.entities)
      this.satellitesDS.entities.add(satEntity)
    },
    initSatEntity(satPositions, satName, color) {
      const sampledPositions =
        this.getPositionsAndCreateSampledPositionsProperty(satPositions)

      return new Cesium.Entity({
        id: satName,
        description: satName,
        position: sampledPositions,
        show: true,
        point: {
          pixelSize: 8,
          color: color,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1
        },
        label: {
          text: satName,
          font: '16px Helvetica',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          pixelOffset: new Cesium.Cartesian2(0, 20),
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
            5e4,
            3e7
          )
        }
      })
    },
    getPositionsAndCreateSampledPositionsProperty(satPositions) {
      const treatedSatPositions =
        CesiumConstellationsTools.createShiftedPosArray(
          satPositions,
          0,
          this.timestep
        )
      const sampledPositionProperty =
        CesiumConstellationsTools.createSampledPositionProperty(
          treatedSatPositions,
          this.getSimulationDates()[0],
          Cesium.ReferenceFrame.INERTIAL
        )
      return sampledPositionProperty
    }
  }
}
</script>

<style scoped>
#cesium-container {
  position: relative;
  height: 500px;
  width: 100%;
  z-index: 2;
}

#blurred-background {
  background-color: black;
  opacity: 0.5;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 3;
}

#loading-indicator {
  position: absolute;
  margin: auto;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 4;
  display: block;
}
</style>
